<script setup lang="ts">
const props = defineProps({
  object: {
    type: Object as PropType<Sanity.Default.Schema.ImageCards>,
    required: true,
  },
})

const { urlFor } = useSanityImage()

const gridCols: ComputedRef<string> = computed(() => {
  if (!props.object?.cards || props.object?.cards.length === 0) {
    return ''
  }
  if (props.object?.cards.length === 1) {
    return 'grid-cols-1'
  }
  if (props.object?.cards.length === 2) {
    return 'grid-cols-2 gap-3 lg:gap-8'
  }
  if (props.object?.cards.length === 3) {
    return 'grid-cols-3 gap-3 lg:gap-6'
  }
  return 'grid-cols-4 gap-2 lg:gap-4'
})

const sizesString: ComputedRef<string> = computed(() => {
  if (!props.object?.cards) {
    return ''
  }
  if (props.object?.cards.length === 1) {
    return 'sizes="100vw"'
  }
  if (props.object?.cards.length === 2) {
    return 'sizes="sm:50vw md:50vw lg:676px"'
  }
  if (props.object?.cards.length % 3 === 0) {
    return 'sizes="sm:33vw md:33vw lg:440px"'
  }
  return 'sizes="sm:25vw md:25vw lg:350px"'
})

const imageWidth: ComputedRef<number> = computed(() => {
  if (!props.object?.cards) {
    return 0
  }
  if (props.object?.cards.length === 1) {
    return 1600
  }
  if (props.object?.cards.length === 2) {
    return 800
  }
  if (props.object?.cards.length % 3 === 0) {
    return 530
  }
  return 352
})

const imageHeight: ComputedRef<number> = computed(() => {
  return Math.ceil(imageWidth.value * (9 / 16))
})
</script>

<template>
  <div v-if="object" class="container-8xl pb-12">
    <div
      class="grid"
      :class="gridCols"
    >
      <div
        v-for="(card, index) in object.cards"
        :key="`card-${index}`"
        class="rounded-lg overflow-hidden bg-white shadow-sm border border-gray-200"
      >
        <nuxt-img
          v-if="card.image"
          :src="urlFor(card.image).width(imageWidth).height(imageHeight).fit('crop').url()"
          :alt="card.image.alt"
          class="relative object-cover object-center w-full h-auto aspect-[16/9]"
          quality="85"
          :width="imageWidth"
          :height="imageHeight"
          :sizes="sizesString"
          loading="lazy"
        />
        <div class="p-4 lg:p-5 xl:p-6">
          <h3 v-if="card.title" class="thin-title">
            {{ card.title }}
          </h3>
          <div v-if="card.description" class="text-gray-700 text-sm">
            {{ card.description }}
          </div>
          <UiBtn
            v-if="card.linkText && card.linkUrl"
            class="mt-4"
            :to="card.linkUrl"
            :padded="false"
            variant="link"
            size="sm"
          >
            {{ card.linkText }}
          </UiBtn>
        </div>
      </div>
    </div>
  </div>
</template>
